import { sendAnalytics } from '@audio-network/analytics'
import { Checkbox } from '@audio-network/componentlibrary'
import { Plus } from '@audio-network/componentlibrary/icons'
import { usePlayerApi } from '@audio-network/player'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useRef, useState } from 'react'

import { useActiveProject } from '@/hooks/useActiveProject'
import useProjectStore from '@/stores/projectStore'
import useSessionStore from '@/stores/sessionStore'

import TrackRow from '../TrackRow/TrackRow'
import ActionBar from './ActionBar/ActionBar'
import DragImage from './DragImage/DragImage'
import { useTrackSelection } from './TrackList.hooks'
import styles from './TrackList.module.css'
import { TrackListProps } from './TrackList.types'

const TrackList = ({
	download,
	isCatalogueAvailable = true,
	source,
	tracks,
	skipQueueInitialisation,
	...props
}: TrackListProps): JSX.Element => {
	const { initialiseQueue } = usePlayerApi()
	const trackRowDragImageRef = useRef<HTMLDivElement>(null)
	const { project, isSimilarTracks } = useActiveProject()
	const addTracksToProject = useProjectStore(
		(state) => state.addTracksToProject
	)
	const [addingTrackIds, setAddingTrackIds] = useState<Set<number>>(new Set())
	const { t: trackrow } = useTranslation(['trackrow'])

	const { user } = useSessionStore((state) => state.session)

	const canMultiselect = !!user?.id && !isSimilarTracks

	const {
		selectedTracks,
		selectedTrackIds,
		handleSelectAll,
		handleClearAll,
		handleTrackSelected,
	} = useTrackSelection({ tracks })

	const areTracksSelected = selectedTracks.length > 0

	const handleToggleSelectAll = (): void =>
		areTracksSelected ? handleClearAll() : handleSelectAll()

	const handleAddSimilarTrack = async (track: Track): Promise<void> => {
		setAddingTrackIds((prev) => new Set(prev).add(track.id))
		try {
			await addTracksToProject(project.Id, [track.id])
			sendAnalytics({
				noun: 'Add to Project',
				pastTenseVerb: 'Completed',
				eventParameters: {
					TrackId: track.id,
					TrackTitle: track.title,
					AlbumNo: track.album.number,
					TrackSource: source,
					Catalogue: track.album.catalogue.name,
					Genre: track.album.genre,
				},
			})
		} catch (error) {
			console.error(error)
		} finally {
			setAddingTrackIds((prev) => {
				const next = new Set(prev)
				next.delete(track.id)
				return next
			})
		}
	}

	useEffect(() => {
		/**
		 * We initialise the player queue when tracks are available/added/removed.
		 */
		if (tracks && tracks.length > 0 && !skipQueueInitialisation) {
			initialiseQueue(tracks)
		}
	}, [tracks, initialiseQueue, skipQueueInitialisation])

	return (
		<>
			<div className={styles.trackListSection}>
				{canMultiselect && (
					<ActionBar
						download={download}
						isCatalogueAvailable={isCatalogueAvailable}
						onClearSelection={handleClearAll}
						onSelectAll={handleToggleSelectAll}
						source={source}
						tracks={tracks}
						selectedTracks={selectedTracks}
					/>
				)}
				<ol className={styles.trackList} {...props}>
					{tracks.map((track, index) => (
						<React.Fragment key={track.id}>
							<li
								className={clsx(
									styles.trackListItem,
									isSimilarTracks && styles.hideNumber
								)}
							>
								{canMultiselect && (
									<div className={styles.checkboxContainer}>
										<Checkbox
											checked={selectedTrackIds.has(track.id)}
											onChange={(event) =>
												handleTrackSelected(event, track, index)
											}
											className={styles.checkbox}
											value={track.title}
											aria-label={track.title}
											data-testid="track-row-checkbox"
											label=""
											name={`track-${track.id}-checkbox`}
										/>
									</div>
								)}
								{isSimilarTracks && (
									<div className={styles.addSimilarTrackButtonContainer}>
										<button
											onClick={() => handleAddSimilarTrack(track)}
											disabled={addingTrackIds.has(track.id)}
										>
											<Plus
												title={trackrow('track-actions.add-to-project')}
												className={styles.addSimilarTrackIcon}
											/>
										</button>
									</div>
								)}
								<TrackRow
									dragImageRef={trackRowDragImageRef}
									track={track}
									source={source}
								/>
							</li>
						</React.Fragment>
					))}
				</ol>
			</div>
			<DragImage ref={trackRowDragImageRef} />
		</>
	)
}

export default TrackList
